/**
 * AccountProvider — web2 (Suikamon 20). Holds the account session and exposes sign-in through
 * Supabase Auth only: email code / magic link / OAuth (useSupabaseLogin). The session
 * (JWT + account_id) is persisted in localStorage so it survives reloads.
 *
 * No wallet provider is mounted any more, so this reads no wallet hook. The wallet logins
 * (Sui SIWS, EVM SIWE, Solana) are PARKED, not deleted, in `web3/WalletAccountProvider.tsx`;
 * their context slots stay so the parked sign-in dialog still compiles, and answer with
 * `WALLET_LOGIN_PARKED` if anything ever calls them.
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { getSession, setSession, clearSession, WEB2_LOGIN_CHAINS, type AccountSession } from './sessionStore';
import { AccountContext, type AccountContextValue } from './useAccountId';
import { getGuestFreePetId, clearGuestFreePet } from './guestPlay';
import { claimFreePets } from '../api/freeAdoptionApi';
import { useSupabaseLogin } from './useSupabaseLogin';
import { useCrazyGamesLogin } from './useCrazyGamesLogin';
import { SignInNotice } from '../components/shared/SignInNotice';

export const WALLET_LOGIN_PARKED = 'Wallet sign-in is not available — sign in with your email.';

async function walletLoginParked(): Promise<void> {
  throw new Error(WALLET_LOGIN_PARKED);
}

/**
 * The stored session, if it is a web2 one (Supabase Auth, or Login with CrazyGames, whose
 * account is a Supabase user too). Web2 hard reset: every login is Supabase Auth, so
 * any other stored session (a wallet login from before the reset, still in a phone's
 * localStorage) is not an identity any more. Restored, it keyed the game on the old wallet and
 * listed every pre-reset Suikamon it had. It is cleared, so nothing — the game or an API client
 * reading the store — can act as that wallet again. The player simply signs in.
 */
function restoreWeb2Session(): AccountSession | null {
  const stored = getSession();
  if (stored && !(stored.chain && WEB2_LOGIN_CHAINS.includes(stored.chain))) {
    clearSession();
    return null;
  }
  return stored;
}

export function AccountProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<AccountSession | null>(restoreWeb2Session);
  const sessionRef = useRef(session);
  // Transient one-time signal: true for the session right after a brand-new account signs in.
  // Not persisted — the App-level guard handles per-account dedup.
  const [activationPending, setActivationPending] = useState(false);

  const persist = useCallback((s: AccountSession) => {
    setSession(s);
    sessionRef.current = s;
    if (!getGuestFreePetId()) {
      setSessionState(s);
      return;
    }
    // Convert-to-own: a guest who played a free starter before signing in has it — and its
    // campaign clears and Soul Shards — moved onto the new account. The game only switches to
    // the account once that claim has settled: flipped first, every page loads the account's
    // roster before the pet has moved and shows it empty. That is what a Google sign-in hit,
    // since its redirect reloads the page and drops any page-level "claim, then refresh".
    // Best-effort — sign-in succeeds even if the claim fails. The marker is cleared only once
    // the pets are really on the account; a refusal or an error keeps it for a later sign-in.
    claimFreePets()
      .then((result) => {
        if (result.success && !result.ineligible) clearGuestFreePet();
      })
      .catch((error) => {
        console.error('Failed to claim free pet after login:', error);
      })
      .finally(() => {
        // A logout (or another account) while the claim ran wins.
        if (sessionRef.current === s) setSessionState(s);
      });
  }, []);

  const dismissActivation = useCallback(() => setActivationPending(false), []);

  const clearAccountSession = useCallback(() => {
    clearSession();
    sessionRef.current = null;
    setSessionState(null);
    setActivationPending(false);
  }, []);

  // Auth callbacks read the ref updated synchronously by persist/clearAccountSession.
  const markNewAccount = useCallback(() => setActivationPending(true), []);
  const { supabaseAuthEnabled, sendEmailCode, verifyEmailCode, loginWithOAuth, signOutSupabase,
    loginError, dismissLoginError, isSigningIn, retryLogin } =
    useSupabaseLogin({ sessionRef, persist, onNewAccount: markNewAccount, onSignedOut: clearAccountSession });
  const { crazyGamesLoginAvailable, loginWithCrazyGames } =
    useCrazyGamesLogin({ sessionRef, persist, onNewAccount: markNewAccount, onSignedOut: clearAccountSession });

  const logout = useCallback(() => {
    signOutSupabase();
    clearAccountSession();
  }, [signOutSupabase, clearAccountSession]);

  const value = useMemo<AccountContextValue>(
    () => ({
      accountId: session?.accountId ?? null,
      token: session?.token ?? null,
      // The player key: the account_id for a web2 account (sessions carry it as `address`).
      address: session?.address ?? null,
      isAuthenticated: Boolean(session),
      email: session?.email ?? null,
      displayName: session?.displayName ?? null,
      loginWithSui: walletLoginParked,
      loginWithEvm: walletLoginParked,
      loginWithSolana: walletLoginParked,
      supabaseAuthEnabled,
      sendEmailCode,
      verifyEmailCode,
      loginWithOAuth,
      crazyGamesLoginAvailable,
      loginWithCrazyGames,
      logout,
      activationPending,
      dismissActivation,
    }),
    [
      session,
      supabaseAuthEnabled,
      sendEmailCode,
      verifyEmailCode,
      loginWithOAuth,
      crazyGamesLoginAvailable,
      loginWithCrazyGames,
      logout,
      activationPending,
      dismissActivation,
    ]
  );

  return <AccountContext.Provider value={value}>
    <SignInNotice
      isSigningIn={Boolean(isSigningIn)}
      failure={loginError ?? null}
      onRetry={() => void retryLogin?.()}
      onDismiss={() => dismissLoginError?.()}
    />
    {children}
  </AccountContext.Provider>;
}
